import { _ } from "js/vendor";
import { app } from "js/namespaces";
import log from "js/core/logger";
import firebase from "js/firebase";

let state = {};

export async function identify(user, extraProps, isNew) {
    const { claims } = await user.getIdTokenResult();
    if (claims.session_tracking_disabled) {
        log.info(log.AUTH)(`[Analytics.identify] ${user.uid} user has the "session_tracking_disabled" custom claim set to true, won't be tracking session`);
        return;
    }

    if (window.Raven) {
        window.Raven.setUserContext({
            email: user.email,
            id: user.uid
        });
    }

    window.dataLayer.push({ userId: user.uid });

    if (window.zE) {
        window.zE(function() {
            window.zE.identify({
                email: user.email,
                name: user.displayName || user.email,
                // TODO: this is going to be incorrect, but Zendesk doesn't update it anyway
                // https://developer.zendesk.com/embeddables/docs/widget/core#identify
                currentPlan: app.user && app.user.analyticsPersonalPlan,
            });
        });
    }

    if (window.amplitude) {
        window.amplitude.getInstance().setUserId(user.uid);
        const userProps = Object.assign({}, extraProps);
        if (isNew) {
            userProps.initial_signup_date = new Date().toISOString();
        }
        setUserProps(userProps);
    }

    if (window.Appcues) {
        window.Appcues.identify(user.uid, {
            createdAt: user.metadata.creationTime,
            planTier: app.user && app.user.analyticsPersonalPlan,
            firstName: user.displayName,
            email: user.email,
            userType: app.user && app.user.get("userInfo") && app.user.get("userInfo")["userType"]
        });
    }

    if (window.debugAnalytics) {
        log.info(log.ANALYTICS)("identify", user.uid);
    }
}

/**
 * Update the state that is sent along with trackEvent().
 */
export function trackState(props) {
    const newState = _.merge({}, state, props);
    if (window.debugAnalytics && !_.isEqual(state, newState)) {
        log.info(log.ANALYTICS)("state", newState);
    }
    state = newState;
}

export function getState(propName) {
    return state[propName];
}

/**
 * Helper to recursively update any state variables which appear in event props.
 */
function updateState(obj, updates, path = []) {
    for (const [key, value1] of Object.entries(obj)) {
        const childPath = [...path, key];
        const value2 = updates[key];
        if (value1 === undefined || value1 === null) {
            continue;
        }
        if (typeof value1 == "object") {
            updateState(value1, value2, childPath);
        } else {
            if (value1 === value2) {
                if (window.debugAnalytics) {
                    console.warn("[ANALYTICS]", `"${childPath.join(".")}"`, "is redundant - tracked in state");
                }
            } else if (updates.hasOwnProperty(key)) {
                if (window.debugAnalytics) {
                    log.info(log.ANALYTICS)("state", childPath.join("."), "->", value2);
                }
                obj[key] = value2;
            }
        }
    }
}

/**
 * Track an event.
 */
export async function track(options) {
    const user = firebase.auth().currentUser;
    if (user) {
        const { claims } = await user.getIdTokenResult();
        if (claims.session_tracking_disabled) {
            log.info(log.AUTH)(`[Analytics.track] ${user.uid} user has the "session_tracking_disabled" custom claim set to true, won't be tracking session`);
            return;
        }
    }

    // merge state into props
    const props = _.merge({}, state, options.props);
    const role = props.role;

    const eventId = options.category + ":" + options.action;
    const firebaseEventId = options.category + "-" + options.action;

    // update any state variables that are changed in props
    updateState(state, options.props);

    if (window.debugAnalytics) {
        log.info(log.ANALYTICS)("track", { ...options, props });
    }

    if (!props.workspace_id) {
        console.warn("[ANALYTICS]", new Error(`options.props.workspace_id required in all events - ${eventId}`));
    }

    const removePrefix = (obj, prefix) => {
        Object.keys(obj).forEach(key => {
            if (key.indexOf(prefix) === 0) {
                obj[key.slice(3)] = obj[key];
                delete obj[key];
            }
        });
    };

    const sp_props = { ...props };
    removePrefix(sp_props, "sp_");
    const pg_props = { ...props };
    removePrefix(pg_props, "pg_");

    const event = {
        event: eventId,
        eventCategory: options.category,
        eventAction: options.action,
        eventLabel: options.label,
        eventProperty: sp_props,
        eventValue: options.value,
        eventUserId: user ? user.uid : null,
        eventRole: role
    };
    const flushEvent = {
        event: "Flush",
        eventCategory: undefined,
        eventAction: undefined,
        eventLabel: undefined,
        eventProperty: undefined,
        eventValue: undefined,
        eventRole: undefined
    };

    if (options.audit) {
        delete pg_props.team_id;
        delete pg_props.affected_user_id;
        delete pg_props.presentation_id;
        delete pg_props.workspace_id;
        pg_props.label = options.label;
        pg_props.value = options.value;

        let columns = {
            category: options.category,
            action: options.action,
            team_id: props.team_id,
            affected_user_id: props.pg_affected_user_id,
            presentation_id: props.presentation_id,
            workspace_id: props.workspace_id,
            props: JSON.stringify(pg_props)
        };

        log.audit("analytics", null, columns);
    }

    if (window.dataLayer) {
        // flush the full event to prevent GTM from re-firing previous event
        window.dataLayer.push(flushEvent);
        window.dataLayer.push(event);
    }

    if (window.amplitude && options.amplitude !== false) {
        window.amplitude.getInstance().logEvent(
            eventId,
            Object.assign(
                {
                    label: options.label,
                    value: options.value
                },
                props
            )
        );
    }

    if (user && user.uid && window.Appcues) {
        window.Appcues.track(eventId, event);
    }
}

export function setUserProps(props) {
    props = props || {};
    if (window.amplitude) {
        let hasUpdates = false;
        let identify = new window.amplitude.Identify();
        for (let key in props) {
            hasUpdates = true;
            if (props.hasOwnProperty(key)) {
                if (props[key] === null) {
                    identify = identify.unset(key);
                }
                identify = identify.set(key, props[key]);
            }
        }

        if (hasUpdates) {
            window.amplitude.getInstance().identify(identify);
        }
    }

    if (window.debugAnalytics) {
        log.info(log.ANALYTICS)("setUserProps", props);
    }
}

export function incUserProps(props) {
    props = props || {};
    if (window.amplitude) {
        let hasUpdates = false;
        let identify = new window.amplitude.Identify();
        for (let key in props) {
            hasUpdates = true;
            if (props.hasOwnProperty(key)) {
                identify = identify.add(key, props[key]);
            }
        }
        if (hasUpdates) {
            window.amplitude.getInstance().identify(identify);
        }
    }

    if (window.debugAnalytics) {
        log.info(log.ANALYTICS)("incUserProps", props);
    }
}

export function getDebugAnalyticsState() {
    return state;
}
